import { api } from '@/lib/api';
import { unwrapApiData } from '@/lib/store-formation';

const DEFAULT_APP_HOSTS = ['localhost', '127.0.0.1'];

/** Header aligné sur le backend (`lyrraone-learner-tenant.ts`). */
export const LO_SITE_HOST_HEADER = 'X-Lo-Site-Host';

function configuredAppHosts(): string[] {
  const raw = import.meta.env.VITE_APP_HOSTNAMES as string | undefined;
  if (!raw?.trim()) return DEFAULT_APP_HOSTS;
  return raw.split(',').map((h) => h.trim().toLowerCase()).filter(Boolean);
}

export function isAppPlatformHostname(hostname: string): boolean {
  const host = hostname.toLowerCase().split(':')[0];
  const appHosts = configuredAppHosts();
  if (appHosts.some((h) => host === h || host.endsWith(`.${h}`))) return true;
  if (host.endsWith('.lyrraone.com') || host === 'lyrraone.com') return true;
  // Ancien domaine .fr (redirections)
  if (host.endsWith('.lyrraone.fr') || host === 'lyrraone.fr') return true;
  return false;
}

export interface CustomDomainSiteInfo {
  siteSlug: string;
  siteName: string;
  creatorId?: string | null;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  whiteLabel?: boolean;
}

export async function fetchSiteByCustomHost(hostname: string): Promise<CustomDomainSiteInfo | null> {
  try {
    const resp = await api.get('/public/lyrraone/sites/by-host', { host: hostname });
    return unwrapApiData<CustomDomainSiteInfo>(resp);
  } catch {
    return null;
  }
}

export function loPublicSitePath(
  siteSlug: string,
  opts?: {
    pageSlug?: string;
    blog?: boolean;
    postSlug?: string;
    formations?: boolean;
    formationId?: string;
    bundleId?: string;
    boutique?: boolean;
    productId?: string;
    communities?: boolean;
    affiliate?: boolean;
    affiliateDashboard?: boolean;
    contact?: boolean;
    lang?: string;
  },
  customDomainSlug?: string | null,
): string {
  const onCustom = customDomainSlug === siteSlug;
  let path: string;
  if (opts?.affiliateDashboard) {
    path = onCustom
      ? '/affiliate/dashboard'
      : `/s/${encodeURIComponent(siteSlug)}/affiliate/dashboard`;
  } else if (opts?.affiliate) {
    path = onCustom ? '/affiliate' : `/s/${encodeURIComponent(siteSlug)}/affiliate`;
  } else if (opts?.contact) {
    path = onCustom ? '/contact' : `/s/${encodeURIComponent(siteSlug)}/contact`;
  } else if (opts?.communities) {
    path = onCustom ? '/communautes' : `/s/${encodeURIComponent(siteSlug)}/communautes`;
  } else if (opts?.boutique && opts.productId) {
    path = onCustom
      ? `/boutique/${encodeURIComponent(opts.productId)}`
      : `/s/${encodeURIComponent(siteSlug)}/boutique/${encodeURIComponent(opts.productId)}`;
  } else if (opts?.boutique) {
    path = onCustom ? '/boutique' : `/s/${encodeURIComponent(siteSlug)}/boutique`;
  } else if (opts?.formations && opts.bundleId) {
    path = onCustom
      ? `/formations/packs/${encodeURIComponent(opts.bundleId)}`
      : `/s/${encodeURIComponent(siteSlug)}/formations/packs/${encodeURIComponent(opts.bundleId)}`;
  } else if (opts?.formations) {
    if (opts.formationId) {
      path = onCustom
        ? `/formations/${encodeURIComponent(opts.formationId)}`
        : `/s/${encodeURIComponent(siteSlug)}/formations/${encodeURIComponent(opts.formationId)}`;
    } else {
      path = onCustom ? '/formations' : `/s/${encodeURIComponent(siteSlug)}/formations`;
    }
  } else if (opts?.blog && opts.postSlug) {
    path = onCustom
      ? `/blog/${encodeURIComponent(opts.postSlug)}`
      : `/s/${encodeURIComponent(siteSlug)}/blog/${encodeURIComponent(opts.postSlug)}`;
  } else if (opts?.blog) {
    path = onCustom ? '/blog' : `/s/${encodeURIComponent(siteSlug)}/blog`;
  } else if (opts?.pageSlug) {
    path = onCustom
      ? `/${encodeURIComponent(opts.pageSlug)}`
      : `/s/${encodeURIComponent(siteSlug)}/${encodeURIComponent(opts.pageSlug)}`;
  } else {
    path = onCustom ? '/' : `/s/${encodeURIComponent(siteSlug)}`;
  }

  if (opts?.lang) {
    path += `${path.includes('?') ? '&' : '?'}lang=${encodeURIComponent(opts.lang)}`;
  }
  return path;
}
